import type { ReactNode } from 'react';
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { AuthProvider, useAuth } from '@core/auth/AuthProvider';
import AuthGate from '@core/auth/AuthGate';
import AppLayout from '@core/components/AppLayout';
import { ToastProvider } from '@core/components/Toast';
import RequireRole from '@core/components/RequireRole';
import OperatorsPage from '@core/masters/OperatorsPage';
import CompaniesPage from '@core/masters/CompaniesPage';
import CsvImportPage from '@core/masters/CsvImportPage';
import BbCredentialsPage from '@core/masters/BbCredentialsPage';
import SettingsUsersPage from '@core/masters/SettingsUsersPage';
import SettingsSystemPage from '@core/masters/SettingsSystemPage';
import HomePage from '@core/dashboard/HomePage';
import StaffMyPage from '@core/staff/StaffMyPage';
import ShiftPage from '@core/staff/ShiftPage';
import PlPage from '@core/dashboard/PlPage';
import DeliveryInputPage from '@core/dashboard/DeliveryInputPage';
import CompaniesSummaryPage from '@core/dashboard/CompaniesSummaryPage';
import StylesSummaryPage from '@core/dashboard/StylesSummaryPage';
import StaffSummaryPage from '@core/dashboard/StaffSummaryPage';
import type { Role } from '@core/lib/types';

/** ロール別リダイレクト(§3): admin=マスタ / viewer=月次 / operator=本人成績 */
function HomeRedirect() {
  const { homePath } = useAuth();
  return <Navigate to={homePath} replace />;
}

/** 画面ごとの許可ロール(firestore.rules と同じ条件を UI 側にも) */
function Guard({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { role } = useAuth();
  return <RequireRole allowed={role !== null && roles.includes(role)}>{children}</RequireRole>;
}

const ADMIN: Role[] = ['admin'];
const ADMIN_VIEWER: Role[] = ['admin', 'viewer'];
const ADMIN_OPERATOR: Role[] = ['admin', 'operator'];

/**
 * データルーター(createBrowserRouter)。未保存の変更があるページの離脱確認(useBlocker)に必要。
 * ログイン状態の出し分け(AuthGate)はレイアウトルートの中で行う
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <AuthGate>
          <AppLayout />
        </AuthGate>
      }
    >
      <Route index element={<HomeRedirect />} />

      <Route
        path="/home"
        element={
          <Guard roles={ADMIN}>
            <HomePage />
          </Guard>
        }
      />

      <Route
        path="/operators"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <OperatorsPage />
          </Guard>
        }
      />
      <Route
        path="/companies"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <CompaniesPage />
          </Guard>
        }
      />
      <Route
        path="/import"
        element={
          <Guard roles={ADMIN}>
            <CsvImportPage />
          </Guard>
        }
      />
      <Route
        path="/bb-credentials"
        element={
          <Guard roles={ADMIN}>
            <BbCredentialsPage />
          </Guard>
        }
      />

      {/* ---- 集計(⑥指示: PL / 会社別 / 出勤形態×報酬区分別 / スタッフ別成績。各ページ内で日次/週次/月次を切替) ---- */}
      <Route
        path="/pl"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <PlPage />
          </Guard>
        }
      />
      {/* 売上実績(納品)ページは廃止し、日報/サマリ に統合(2026-09-10 指示) */}
      <Route path="/summary/sales" element={<Navigate to="/summary/deliveries" replace />} />
      <Route
        path="/summary/deliveries"
        element={
          <Guard roles={ADMIN}>
            <DeliveryInputPage />
          </Guard>
        }
      />
      <Route
        path="/summary/companies"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <CompaniesSummaryPage />
          </Guard>
        }
      />
      <Route
        path="/summary/styles"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <StylesSummaryPage />
          </Guard>
        }
      />
      <Route
        path="/summary/staff"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <StaffSummaryPage />
          </Guard>
        }
      />
      {/* 旧URL */}
      <Route path="/daily" element={<Navigate to="/pl?mode=day" replace />} />
      <Route path="/monthly" element={<Navigate to="/pl?mode=month" replace />} />

      {/* ---- プレースホルダ(後続Phaseで実装) ---- */}
      <Route
        path="/staff"
        element={
          <Guard roles={ADMIN_OPERATOR}>
            <StaffMyPage />
          </Guard>
        }
      />
      <Route
        path="/shift"
        element={
          <Guard roles={ADMIN_OPERATOR}>
            <ShiftPage />
          </Guard>
        }
      />

      <Route
        path="/settings/users"
        element={
          <Guard roles={ADMIN}>
            <SettingsUsersPage />
          </Guard>
        }
      />
      <Route
        path="/settings/system"
        element={
          <Guard roles={ADMIN}>
            <SettingsSystemPage />
          </Guard>
        }
      />

      <Route path="*" element={<HomeRedirect />} />
    </Route>,
  ),
);

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </AuthProvider>
  );
}
